import { logger } from '@/shared/lib/logger';

/**
 * Audio Playback Service
 *
 * Handles playback of base64-encoded Ogg/Opus audio chunks received from the voice API.
 * Uses HTML5 Audio Element for native browser support and simplicity.
 */

export interface AudioPlaybackOptions {
  autoPlay?: boolean;
  volume?: number;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onError?: (error: Error) => void;
}

export class AudioPlaybackService {
  private audioElement: HTMLAudioElement | null = null;
  private objectUrl: string | null = null;

  /**
   * Decodes base64-encoded audio chunk and creates a playable Blob
   * Tries multiple decoding strategies
   */
  private decodeBase64ToBlob(base64Audio: string): Blob {
    logger.debug('Attempting to decode audio chunk, length:', base64Audio.length);
    logger.debug('First 200 chars:', base64Audio.substring(0, 200));
    
    // Strategy 1: Standard base64 decoding
    try {
      return this.tryStandardBase64Decode(base64Audio);
    } catch (error1) {
      logger.warn('Standard base64 decode failed:', error1);
      
      // Strategy 2: URL-safe base64 decoding
      try {
        return this.tryUrlSafeBase64Decode(base64Audio);
      } catch (error2) {
        logger.warn('URL-safe base64 decode failed:', error2);
        
        // Strategy 3: Assume it's already binary data
        try {
          return this.tryDirectBinaryDecode(base64Audio);
        } catch (error3) {
          logger.error('All decoding strategies failed');
          throw new Error(`Failed to decode audio data. Tried standard base64, URL-safe base64, and direct binary. Last error: ${error3 instanceof Error ? error3.message : 'Unknown'}`);
        }
      }
    }
  }

  private tryStandardBase64Decode(base64Audio: string): Blob {
    // Remove any whitespace, newlines
    let cleanBase64 = base64Audio.replace(/\s/g, '');
    
    // Remove data URL prefix if present
    if (cleanBase64.includes(',')) {
      cleanBase64 = cleanBase64.split(',')[1];
    }
    
    // Remove any non-base64 characters
    cleanBase64 = cleanBase64.replace(/[^A-Za-z0-9+/=]/g, '');
    
    // Validate base64 string length (must be multiple of 4)
    while (cleanBase64.length % 4 !== 0) {
      cleanBase64 += '=';
    }
    
    logger.debug('Trying standard base64, cleaned length:', cleanBase64.length);
    
    const binaryString = atob(cleanBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    logger.debug('Standard decode successful:', bytes.length, 'bytes');
    return new Blob([bytes], { type: 'audio/ogg; codecs=opus' });
  }

  private tryUrlSafeBase64Decode(base64Audio: string): Blob {
    // Convert URL-safe base64 to standard base64
    let cleanBase64 = base64Audio
      .replace(/\s/g, '')
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    // Remove data URL prefix if present
    if (cleanBase64.includes(',')) {
      cleanBase64 = cleanBase64.split(',')[1];
    }
    
    // Add padding if needed
    while (cleanBase64.length % 4 !== 0) {
      cleanBase64 += '=';
    }
    
    logger.debug('Trying URL-safe base64, cleaned length:', cleanBase64.length);
    
    const binaryString = atob(cleanBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    logger.debug('URL-safe decode successful:', bytes.length, 'bytes');
    return new Blob([bytes], { type: 'audio/ogg; codecs=opus' });
  }

  private tryDirectBinaryDecode(data: string): Blob {
    logger.debug('Trying direct binary decode');
    
    // Assume the data is already in binary format (UTF-8 encoded binary)
    const bytes = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
      bytes[i] = data.charCodeAt(i) & 0xff;
    }
    
    logger.debug('Direct binary decode:', bytes.length, 'bytes');
    return new Blob([bytes], { type: 'audio/ogg; codecs=opus' });
  }

  /**
   * Plays audio from a base64-encoded string
   */
  async playBase64Audio(base64Audio: string, options: AudioPlaybackOptions = {}): Promise<void> {
    // Clean up any existing audio
    this.cleanup();

    try {
      // Decode base64 to Blob
      const audioBlob = this.decodeBase64ToBlob(base64Audio);
      
      // Create object URL from Blob
      this.objectUrl = URL.createObjectURL(audioBlob);
      
      // Create audio element
      this.audioElement = new Audio(this.objectUrl);
      
      // Set volume (default to 1.0)
      this.audioElement.volume = options.volume ?? 1.0;
      
      // Track whether playback completed to suppress spurious post-ended errors
      let hasEnded = false;

      // Attach event listeners
      if (options.onPlay) {
        this.audioElement.addEventListener('play', options.onPlay);
      }
      
      if (options.onPause) {
        this.audioElement.addEventListener('pause', options.onPause);
      }
      
      if (options.onEnded) {
        this.audioElement.addEventListener('ended', () => {
          hasEnded = true;
          options.onEnded?.();
          this.cleanup();
        });
      }
      
      if (options.onError) {
        this.audioElement.addEventListener('error', () => {
          // Some browsers fire 'error' after 'ended' when src is cleared during
          // cleanup — ignore it if the audio already finished successfully.
          if (hasEnded) return;
          const error = new Error(`Audio playback error: ${this.audioElement?.error?.message || 'Unknown error'}`);
          options.onError?.(error);
          this.cleanup();
        });
      }
      
      // Auto-cleanup on ended if no custom handler
      if (!options.onEnded) {
        this.audioElement.addEventListener('ended', () => {
          hasEnded = true;
          this.cleanup();
        });
      }
      
      // Play audio
      if (options.autoPlay !== false) {
        await this.audioElement.play();
      }
    } catch (error) {
      this.cleanup();
      throw error;
    }
  }

  /**
   * Plays audio from multiple base64-encoded chunks (for streaming)
   */
  async playBase64AudioChunks(base64Chunks: string[], options: AudioPlaybackOptions = {}): Promise<void> {
    if (base64Chunks.length === 0) {
      throw new Error('No audio chunks provided');
    }

    logger.debug('Processing', base64Chunks.length, 'audio chunks');
    
    // Try to decode each chunk individually first to see which one fails
    const decodedChunks: Uint8Array[] = [];
    
    for (let i = 0; i < base64Chunks.length; i++) {
      try {
        logger.debug(`Decoding chunk ${i + 1}/${base64Chunks.length}`);
        const blob = this.decodeBase64ToBlob(base64Chunks[i]);
        const arrayBuffer = await blob.arrayBuffer();
        decodedChunks.push(new Uint8Array(arrayBuffer));
      } catch (error) {
        logger.error(`Failed to decode chunk ${i + 1}:`, error);
        logger.debug(`Problematic chunk ${i + 1} data:`, base64Chunks[i].substring(0, 500));
        throw error;
      }
    }
    
    // Concatenate all decoded chunks
    const totalLength = decodedChunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const concatenated = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of decodedChunks) {
      concatenated.set(chunk, offset);
      offset += chunk.length;
    }
    
    // Create final blob and play
    const finalBlob = new Blob([concatenated], { type: 'audio/ogg; codecs=opus' });
    this.cleanup();
    this.objectUrl = URL.createObjectURL(finalBlob);
    this.audioElement = new Audio(this.objectUrl);
    
    // Set volume
    this.audioElement.volume = options.volume ?? 1.0;
    
    // Track whether playback completed to suppress spurious post-ended errors
    let hasEnded = false;

    // Attach event listeners
    if (options.onPlay) {
      this.audioElement.addEventListener('play', options.onPlay);
    }
    
    if (options.onPause) {
      this.audioElement.addEventListener('pause', options.onPause);
    }
    
    if (options.onEnded) {
      this.audioElement.addEventListener('ended', () => {
        hasEnded = true;
        options.onEnded?.();
        this.cleanup();
      });
    }
    
    if (options.onError) {
      this.audioElement.addEventListener('error', () => {
        // Some browsers fire 'error' after 'ended' when src is cleared during
        // cleanup — ignore it if the audio already finished successfully.
        if (hasEnded) return;
        const error = new Error(`Audio playback error: ${this.audioElement?.error?.message || 'Unknown error'}`);
        options.onError?.(error);
        this.cleanup();
      });
    }
    
    // Auto-cleanup on ended if no custom handler
    if (!options.onEnded) {
      this.audioElement.addEventListener('ended', () => {
        hasEnded = true;
        this.cleanup();
      });
    }
    
    // Play audio
    if (options.autoPlay !== false) {
      await this.audioElement.play();
    }
  }

  /**
   * Pauses the currently playing audio
   */
  pause(): void {
    if (this.audioElement && !this.audioElement.paused) {
      this.audioElement.pause();
    }
  }

  /**
   * Resumes the paused audio
   */
  async resume(): Promise<void> {
    if (this.audioElement && this.audioElement.paused) {
      await this.audioElement.play();
    }
  }

  /**
   * Stops the audio and cleans up resources
   */
  stop(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
    }
    this.cleanup();
  }

  /**
   * Gets the current playback state
   */
  isPlaying(): boolean {
    return this.audioElement !== null && !this.audioElement.paused;
  }

  /**
   * Gets the current playback time in seconds
   */
  getCurrentTime(): number {
    return this.audioElement?.currentTime ?? 0;
  }

  /**
   * Gets the total duration in seconds
   */
  getDuration(): number {
    return this.audioElement?.duration ?? 0;
  }

  /**
   * Sets the volume (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    if (this.audioElement) {
      this.audioElement.volume = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Cleans up audio resources
   */
  private cleanup(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.src = '';
      this.audioElement = null;
    }
    
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  /**
   * Ensures cleanup on service destruction
   */
  destroy(): void {
    this.cleanup();
  }
}

// Singleton instance for global use
let audioPlaybackInstance: AudioPlaybackService | null = null;

/**
 * Gets or creates the singleton AudioPlaybackService instance
 */
export const getAudioPlaybackService = (): AudioPlaybackService => {
  if (!audioPlaybackInstance) {
    audioPlaybackInstance = new AudioPlaybackService();
  }
  return audioPlaybackInstance;
};

/**
 * Convenience function to play base64 audio
 */
export const playBase64Audio = async (
  base64Audio: string,
  options?: AudioPlaybackOptions
): Promise<void> => {
  const service = getAudioPlaybackService();
  await service.playBase64Audio(base64Audio, options);
};

/**
 * Convenience function to play multiple base64 audio chunks
 */
export const playBase64AudioChunks = async (
  base64Chunks: string[],
  options?: AudioPlaybackOptions
): Promise<void> => {
  const service = getAudioPlaybackService();
  await service.playBase64AudioChunks(base64Chunks, options);
};